import React, { ReactNode, useState } from 'react';
import { alpha, Box, Button, Chip, CircularProgress, keyframes, Paper, Skeleton, Stack, TextField, Theme, Typography } from '@mui/material';
import { CloseCircleFilled, WarningFilled } from '@ant-design/icons';
import { REDUCED_MOTION } from 'components/ui/tokens';
import { offerPercent } from 'features/cancellation/offers';
import { PAYMENT_TAB, useCheckoutState, useCheckoutStore } from '../store';
import { NotePanel } from '../NotePanel';
import {
  ADDONS,
  BILLING_CYCLES,
  CHECKOUT_COPY,
  COUPON_STATUS,
  CYCLE_LABEL,
  CYCLE_SUFFIX,
  EA_HOSTING_PLAN,
  EA_HOSTING_PRODUCT,
  SIGNAL_MANAGER_BASE_SEATS,
  ORIGINAL_DISCOUNT_RE,
  chargeLinesAreNet,
  creditLinesCarryDiscount,
  resolveEffectiveCredit,
  shortenStripeDescription,
  summarizeInvoiceLines,
  toDisplayLines
} from 'lib/billing';
import { formatCents } from '../format';
import { useAmountDueNow, useInvoicePreview, usePlanHasPurchaseOps, type InvoicePreviewLine as PreviewLine } from '../store';

// The summary's tinted panels (credits, charges, the discount warning): a light fill and an edge in one colour.
const tonedPanelSx = (tone: 'success' | 'primary' | 'warning', fill: number, edge: number) => (theme: Theme) => ({
  p: 1.25,
  borderRadius: 1.75,
  bgcolor: alpha(theme.palette[tone].main, fill),
  border: `1px solid ${alpha(theme.palette[tone].main, edge)}`
});

// A few beats when the "doesn't carry over" warning appears, so it is noticed, then it rests.
const warningPulse = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(1.25); }
`;

// A credit's "(originally X% off)" marker takes the warning's colour: it is why the seats re-bill at standard rate.
function CreditLabel({ text }: { text: string }) {
  const marker = text.match(ORIGINAL_DISCOUNT_RE)?.[0];
  if (!marker) return <>{text}</>;
  const at = text.indexOf(marker);
  return (
    <>
      {text.slice(0, at)}
      <Box component="span" sx={{ color: 'warning.dark', fontWeight: 600 }}>
        {marker}
      </Box>
      {text.slice(at + marker.length)}
    </>
  );
}

function Row({
  label,
  value,
  muted,
  bold,
  tag,
  teal,
  hint
}: {
  label: ReactNode;
  value: string;
  muted?: boolean;
  bold?: boolean;
  /** Small tag beside the label, in the row's tone: teal on a saving, blue otherwise. */
  tag?: string;
  /** Teal (theme success) amount — the positive-accent tone the summary uses
   *  for credits/savings. Twin of the marketing checkout's DiscountRow. */
  teal?: boolean;
  /** Sub-line under the label. Used to name the per-account unit behind a
   *  line total, so the amount on the right can be checked at a glance. */
  hint?: string;
}) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1.5}>
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography
            variant="caption"
            color={muted ? 'text.secondary' : 'text.primary'}
            sx={{ fontSize: 12.5, fontWeight: bold ? 600 : 400 }}
          >
            {label}
          </Typography>
          {tag && (
            <Chip
              size="small"
              label={tag}
              sx={{
                height: 14,
                fontSize: 9.5,
                fontWeight: 600,
                bgcolor: teal ? 'success.lighter' : 'info.lighter',
                color: teal ? 'success.dark' : 'info.dark'
              }}
            />
          )}
        </Stack>
        {hint && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11, lineHeight: 1.35, fontVariantNumeric: 'tabular-nums' }}>
            {hint}
          </Typography>
        )}
      </Stack>
      <Typography
        variant="caption"
        sx={{
          fontSize: 12.5,
          fontWeight: bold || teal ? 600 : 500,
          fontVariantNumeric: 'tabular-nums',
          color: teal ? 'success.main' : muted ? 'text.secondary' : 'text.primary'
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

type GroupedLines = { credits: PreviewLine[]; charges: PreviewLine[] };

function groupPreviewLines(lines: PreviewLine[]): GroupedLines {
  const credits: PreviewLine[] = [];
  const charges: PreviewLine[] = [];
  for (const l of lines) {
    if ((l.amount || 0) < 0) credits.push(l);
    else charges.push(l);
  }
  return { credits, charges };
}

function formatPeriodDate(ts?: { seconds?: number } | null): string | null {
  const secs = Number(ts?.seconds || 0);
  if (!secs) return null;
  try {
    return new Date(secs * 1000).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return null;
  }
}

// Discount-code entry, rendered inside the ledger area. Collapsed to a subtle
// text prompt by default; expands to a small input + Apply. An applied code
// shows as a removable chip. The code itself lives in `useCheckoutStore.coupon`
// so the invoice preview re-quotes with it and the Payment step sends it on
// CreateSubscription. The resulting "Discount" ledger row is the existing
// `preview.discount` row above — this control never renders an amount itself.
// Stands in for the code entry while a cancel offer waits (the offer never stacks with a code): says where the
// offer goes when this cart does not use it. The discount row itself names the offer when it does.
function CancelOfferNote({ percentBps, applied }: { percentBps: number; applied: boolean }) {
  const COPY = CHECKOUT_COPY.summary.cancelOffer;
  const payingByCrypto = useCheckoutStore((s) => s.paymentTab === PAYMENT_TAB.CRYPTO);
  if (applied) return null;
  const percent = offerPercent(percentBps);
  return (
    <Typography variant="caption" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', lineHeight: 1.4, pt: 0.25 }}>
      {payingByCrypto ? COPY.cardOnly(percent) : COPY.waiting(percent)}
    </Typography>
  );
}

function DiscountCodeField({ showDiscountLedger }: { showDiscountLedger: boolean }) {
  const COPY = CHECKOUT_COPY.summary.discountCode;
  const coupon = useCheckoutStore((s) => s.coupon);
  const setCoupon = useCheckoutStore((s) => s.setCoupon);
  // Server verdict for the applied code (public discount-code endpoint, via
  // useCouponValidationSync). 'pending' means the round-trip is in flight —
  // the chip shows a spinner and every other note holds until it settles.
  // 'invalid' means the backend actually said {valid:false}, so the code can
  // be called bad immediately, without waiting for the debounced preview and
  // even on all-trial carts whose $0 quote can't expose a bad code. 'valid'
  // keeps the preview authoritative for amounts, and 'unknown'/null (network
  // failure, base URL unconfigured) leaves the inference below untouched.
  const couponInfo = useCheckoutStore((s) => s.couponInfo);
  const { preview, loading: previewLoading, quotedCoupon } = useInvoicePreview();
  // Right after Apply the settled preview is still the PREVIOUS quote (the
  // re-quote sits behind a 350ms debounce + network, and loading only flips
  // inside the timer), and couponInfo is momentarily null before the
  // validation effect stores 'pending'. Every preview-based judgement below
  // must therefore check the quote was priced with the CURRENT code, and the
  // inference-only paths additionally wait for a settled verdict — otherwise
  // the stale zero-discount quote flashes "couldn't apply that code".
  const previewQuotedWithCoupon = preview != null && quotedCoupon === coupon;
  const couponSettled = couponInfo != null && couponInfo.status !== COUPON_STATUS.pending;
  // A discount code rides every purchase op — creates at subscription level,
  // adds/modifies at item level on the changed line (billing-manager scopes it
  // server-side). Only a cart with no purchase ops at all (e.g. removal-only)
  // has nothing for a code to ride. Recomputed by useInvoicePreviewSync on
  // every cart change, so emptying the cart after applying a code flips this
  // (and the note below) live.
  const hasPurchaseOps = usePlanHasPurchaseOps();
  // Cart has BOTH a trialing line and something charged today. The all-trial
  // case is covered by trialZeroDue below; this is the case a $0-due check
  // cannot see.
  const trialState = useCheckoutState() as any;
  const trialKeys: Set<string> = trialState.trialProductKeys instanceof Set ? trialState.trialProductKeys : new Set();
  const mixedTrialCart = Boolean(coupon) && hasPurchaseOps && trialKeys.size > 0 && (preview?.dueNow || 0) > 0;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  const apply = () => {
    const code = draft.trim();
    if (!code) return;
    setCoupon(code);
    setDraft('');
    setOpen(false);
  };

  // Validation round-trip in flight: the chip swaps its look for a spinner and
  // a muted "Checking code…" caption. Neither the notRecognized warning nor
  // the optimistic note may show while the verdict is pending.
  const couponPending = Boolean(coupon) && couponInfo?.status === COUPON_STATUS.pending;
  // Verified-bad code: show notRecognized right away (same hasPurchaseOps
  // gate as every other note — the field is hidden on purchase-less carts).
  const couponVerifiedInvalid = Boolean(coupon) && hasPurchaseOps && couponInfo?.status === COUPON_STATUS.invalid;
  // Without a server verdict, only judge the code once a re-quote has settled
  // AND the invoice actually charges something: an all-trial cart's $0 invoice
  // legitimately carries no discount (BM skips the coupon on trial creates),
  // so discount===0 there says nothing about whether Stripe recognized the name.
  const notRecognized =
    couponVerifiedInvalid ||
    (Boolean(coupon) &&
      hasPurchaseOps &&
      couponSettled &&
      couponInfo?.status === COUPON_STATUS.unknown &&
      !previewLoading &&
      previewQuotedWithCoupon &&
      (preview!.dueNow || 0) > 0 &&
      (preview!.discount || 0) === 0);
  // A settled $0-due quote with no discount is the all-trial case: the backend
  // ignores the coupon on trial creates entirely, so promising "applied at
  // payment" would be false — state the trial limitation instead. A
  // verified-bad code replaces this neutral note with notRecognized above.
  const trialZeroDue =
    !couponVerifiedInvalid &&
    Boolean(coupon) &&
    hasPurchaseOps &&
    !couponPending &&
    !previewLoading &&
    previewQuotedWithCoupon &&
    (preview!.dueNow || 0) === 0 &&
    (preview!.discount || 0) === 0;
  // Server says the code is real, the re-quote settled with it, the invoice
  // charges something — and the discount is still zero. The code is valid but
  // covers nothing here: a product- or price-scoped code whose allowlist
  // misses every changed line, or one this customer has already redeemed
  // (once-per-customer is enforced server-side and the anonymous validation
  // endpoint can't know who is asking). Sibling of notRecognized above, which
  // handles the same shape when the server verdict was never obtained.
  // Stripe stamps discounted proration lines with "(with N% off)" in the
  // description it generates. If a CHARGE line admits a discount, the code
  // plainly applies — so the not-applicable caption must stay silent even
  // when the reply's discount FIELD reads zero (backend versions that predate
  // the gross-twin fix can't report item-level discount amounts on previews).
  // Only positive lines count: an unused-time CREDIT line carries the marker
  // whenever the PREVIOUS purchase was discounted, which says nothing about
  // this one (e.g. a code the customer already redeemed — its old credit
  // shows "off" while the new charge is full price).
  // Suppression only: this never renders a discount row or amount by itself.
  const linesShowDiscount = chargeLinesAreNet(preview);
  const couponNotApplicable =
    Boolean(coupon) &&
    hasPurchaseOps &&
    couponSettled &&
    couponInfo?.status === COUPON_STATUS.valid &&
    !previewLoading &&
    previewQuotedWithCoupon &&
    !linesShowDiscount &&
    (preview!.dueNow || 0) > 0 &&
    (preview!.discount || 0) === 0;
  // The "Discount" ledger row is the field's positive feedback — rendered for
  // a live invoice AND for the discounted-preview state (first-time buyers,
  // no anchor sub). Only when neither can render does the fail-open note
  // reassure that the code rides along to payment.
  const discountRowVisible = showDiscountLedger && previewQuotedWithCoupon && (preview!.discount || 0) !== 0;
  const showAppliedNote =
    Boolean(coupon) &&
    hasPurchaseOps &&
    couponSettled &&
    !previewLoading &&
    previewQuotedWithCoupon &&
    !notRecognized &&
    !trialZeroDue &&
    !couponNotApplicable &&
    !discountRowVisible;

  if (coupon) {
    // A teal chip above a "couldn't apply that code" warning would contradict
    // it — verified-invalid flips the chip to warning tones with an error ✕;
    // while the verdict is in flight the chip leads with a spinner instead.
    // Twin of the marketing checkout's applied-chip states.
    return (
      <Stack spacing={0.5} sx={{ pt: 0.25 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Chip
            size="small"
            label={coupon}
            icon={
              couponPending ? (
                <CircularProgress size={11} thickness={5} sx={{ color: 'inherit' }} />
              ) : couponVerifiedInvalid ? (
                <CloseCircleFilled aria-hidden style={{ fontSize: 11 }} />
              ) : undefined
            }
            onDelete={() => setCoupon('')}
            deleteIcon={<CloseCircleFilled aria-label={COPY.removeAria} style={{ fontSize: 12 }} />}
            sx={{
              height: 20,
              fontSize: 10.5,
              fontWeight: 600,
              bgcolor: couponVerifiedInvalid ? 'warning.lighter' : 'primary.lighter',
              color: couponVerifiedInvalid ? 'warning.dark' : 'primary.dark',
              border: '1px solid',
              borderColor: couponVerifiedInvalid ? 'warning.light' : 'primary.light',
              '& .MuiChip-icon': { color: 'inherit', ml: 0.5, mr: -0.25 }
            }}
          />
        </Stack>
        {couponPending && hasPurchaseOps && (
          <Typography variant="caption" role="status" aria-live="polite" sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.4 }}>
            {COPY.checking}
          </Typography>
        )}
        {!hasPurchaseOps && (
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.4 }}>
            {COPY.noPurchases}
          </Typography>
        )}
        {notRecognized && (
          <Typography variant="caption" sx={{ fontSize: 11, color: 'warning.main', lineHeight: 1.4 }}>
            {COPY.notRecognized}
          </Typography>
        )}
        {couponNotApplicable && (
          <Typography variant="caption" sx={{ fontSize: 11, color: 'warning.main', lineHeight: 1.4 }}>
            {COPY.notApplicable}
          </Typography>
        )}
        {trialZeroDue && (
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.4 }}>
            {COPY.trialNoDiscount}
          </Typography>
        )}
        {/* Mixed cart: the code discounts the paid lines and can never reach the
            trialing one — billing-manager skips the coupon on trial creates and
            nothing carries over at conversion. Say so where the code is entered,
            rather than letting the buyer infer it from a total. */}
        {mixedTrialCart && (
          <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.4 }}>
            {COPY.trialItemsNotDiscounted}
          </Typography>
        )}
        {showAppliedNote && (
          <Typography variant="caption" sx={{ fontSize: 11, color: 'success.main', lineHeight: 1.4 }}>
            {COPY.appliedAtPayment(coupon)}
          </Typography>
        )}
      </Stack>
    );
  }

  // Nothing in the cart a code could apply to — hide the entry prompt rather
  // than invite a code that execution would silently ignore.
  if (!hasPurchaseOps) return null;

  if (!open) {
    return (
      <Typography
        component="button"
        type="button"
        variant="caption"
        onClick={() => setOpen(true)}
        sx={{
          alignSelf: 'flex-start',
          p: 0,
          border: 0,
          background: 'none',
          cursor: 'pointer',
          fontSize: 11.5,
          color: 'text.secondary',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          textUnderlineOffset: 3,
          '&:hover': { color: 'primary.main' }
        }}
      >
        {COPY.prompt}
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pt: 0.25 }}>
      <TextField
        size="small"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            apply();
          }
        }}
        placeholder={COPY.placeholder}
        inputProps={{ 'aria-label': COPY.inputAria }}
        sx={{
          flex: 1,
          maxWidth: 200,
          '& .MuiInputBase-input': { fontSize: 12.5, py: 0.75 }
        }}
      />
      <Button size="small" variant="outlined" onClick={apply} disabled={!draft.trim()} sx={{ fontSize: 12, px: 1.5, minWidth: 0 }}>
        {COPY.apply}
      </Button>
    </Stack>
  );
}

export function TotalSummary() {
  const state = useCheckoutState();
  const { preview, loading: previewLoading, error: previewError, quotedCoupon } = useInvoicePreview();
  const couponInfo = useCheckoutStore((s) => s.couponInfo);

  const currency = state.currency;
  const cycle = state.billingCycle;
  const suffix = (CYCLE_SUFFIX as any)[cycle] || '';
  const cycleLabel = (CYCLE_LABEL as any)[cycle] || '';
  const isMonthly = cycle === BILLING_CYCLES.monthly;

  // Shared "due today" derivation (preview vs catalog, credit, trial, new-DE
  // charge) — see useAmountDueNow. PaymentStep consumes the same hook.
  const { dueCents, showLiveInvoice, trialApplies, availableCredit, dedicatedOnlyModify, newDedicatedCents, discountedPreviewApplies } =
    useAmountDueNow();
  // The Subtotal/Discount ledger renders from the preview's Stripe-real cents
  // in two states: the classic live invoice (existing sub) and the
  // discount-scoped relaxation for first-time buyers — an applied code whose
  // settled preview carries a real discount. `dueCents` above already uses the
  // preview figure in that state, so ledger and total always agree. Every
  // no-discount path leaves this equal to showLiveInvoice (no display change).
  const showDiscountLedger = showLiveInvoice || discountedPreviewApplies;
  // Reversed seats were bought under an earlier discount and this cart has none, so the seats kept re-bill
  // at standard rate. Shown on a seat cut too, whose credit leaves the subtotal at or below zero.
  const discountNotCarried = showDiscountLedger && preview != null && (preview.discount || 0) === 0 && creditLinesCarryDiscount(preview);
  // Terms of the applied code, for the extension line under the Discount row
  // ("flash — 30% off · this invoice only"). couponInfo is the server verdict;
  // absent/unverified codes just show the code name.
  const discountTerms = [
    couponInfo?.percentOff
      ? `${couponInfo.percentOff}% off`
      : couponInfo?.amountOff
      ? `${formatCents(couponInfo.amountOff, couponInfo.currency || currency)} off`
      : null,
    (CHECKOUT_COPY.summary.discountTerms as Record<string, string>)[couponInfo?.duration || ''] || null
  ]
    .filter(Boolean)
    .join(' · ');
  const trialDays = Number((state as any).trialDays) || 0;
  // Free trial → $0 due today; the cycle total (`afterTrialCents`) bills when
  // the trial ends. Only meaningful on the catalog/new-user path (existing
  // subscribers use the live preview, which the BE already trials/prorates).
  const afterTrialCents = Number(state.grandTotalCycleCents || 0);
  const showAfterTrial = !showLiveInvoice && trialApplies && afterTrialCents > 0;

  const wouldBeCents = state.grandTotalWouldBeCycleCents || 0;
  const savingsCents = state.grandTotalSavingsCents || 0;
  // Hide the strikethrough/savings on trial — a "$0 today" line with a slashed
  // price reads as confusing; savings belong to the post-trial recurring price.
  // Same suppression for the discounted-preview state: the total there is a
  // Stripe-real first-invoice figure, so a catalog "would be" strikethrough
  // beside it would mix provenances (the explicit Subtotal/Discount ledger
  // already tells the savings story).
  const showSavings = !isMonthly && savingsCents > 0 && !showLiveInvoice && !trialApplies && !discountedPreviewApplies;
  const hasCredit = availableCredit > 0;
  // One consistent label across all cycles. Previously monthly showed "Monthly
  // cost" while other cycles showed "To pay now (per year)" — the amount already
  // carries the /mo · /yr suffix, so the per-cycle wording was redundant + noisy.
  const dueTodayLabel =
    trialApplies || showLiveInvoice || state.isModifyingExisting || hasCredit || discountedPreviewApplies
      ? CHECKOUT_COPY.summary.dueTodayTitle
      : CHECKOUT_COPY.rowToPay;

  // While the live preview is recomputing for an authenticated user, suppress
  // the catalog fallback to avoid a confusing "monthly cost" flicker. Skeleton
  // rows hold the layout until the new prorated lines arrive.
  const isUpdatingLivePreview = !!previewLoading;

  // ── Build catalog line breakdown for new-user / fallback path ──────────
  const catalogLines = buildCatalogLines(state, suffix, currency);

  // ── Live preview line breakdown ────────────────────────────────────────
  // Display-prepared lines: marked charge lines grossed back up so they sum to
  // the Subtotal the Discount row subtracts from, credits reworded so an old
  // discount doesn't read as a new one. See toDisplayLines.
  const previewLines: PreviewLine[] = toDisplayLines(preview) as PreviewLine[];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 0,
        borderRadius: 2,
        bgcolor: 'background.paper',
        overflow: 'hidden'
      }}
    >
      {/* ── HEADER ───────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ px: 2.25, pt: 2, pb: 1.25 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" fontWeight={700} sx={{ letterSpacing: -0.1 }}>
            {showLiveInvoice ? CHECKOUT_COPY.summary.previewTitle : CHECKOUT_COPY.summary.orderTitle}
          </Typography>
          {previewLoading && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CircularProgress size={10} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                {CHECKOUT_COPY.summary.updating}
              </Typography>
            </Stack>
          )}
        </Stack>
        {state.trialApplies && (
          <Chip
            size="small"
            label={CHECKOUT_COPY.trialShortNote(state.trialDays)}
            sx={{
              height: 20,
              fontSize: 10.5,
              fontWeight: 600,
              bgcolor: 'success.lighter',
              color: 'success.dark',
              border: '1px solid',
              borderColor: 'success.light'
            }}
          />
        )}
      </Stack>

      {previewError != null && (
        <Box sx={{ px: 2.25, pb: 1.25 }}>
          <NotePanel severity="warning" body={CHECKOUT_COPY.summary.previewError} />
        </Box>
      )}

      {/* ── LINE ITEMS ──────────────────────────────────────────── */}
      <Box sx={{ px: 2.25, pb: 1.5 }}>
        <Stack spacing={1.25}>
          {isUpdatingLivePreview ? (
            <Stack spacing={0.75}>
              <Skeleton variant="text" width="55%" height={14} />
              <Skeleton variant="text" width="80%" height={14} />
              <Skeleton variant="text" width="70%" height={14} />
            </Stack>
          ) : showLiveInvoice ? (
            (() => {
              const { credits, charges } = groupPreviewLines(previewLines);
              const removedSeats = Number((state as any).removedSeats) || 0;
              return (
                <Stack spacing={1.25}>
                  {removedSeats > 0 && <Row label={`${removedSeats} seat${removedSeats > 1 ? 's' : ''} removed`} value="" muted />}
                  {credits.length > 0 && (
                    <Box sx={tonedPanelSx('success', 0.06, 0.18)}>
                      <Stack spacing={0.5}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Box
                            sx={(theme) => ({
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: theme.palette.success.main
                            })}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              color: 'success.dark',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5
                            }}
                          >
                            {CHECKOUT_COPY.summary.creditsSectionLabel}
                          </Typography>
                        </Stack>
                        {credits.map((l, i) => (
                          <Box key={`cr-${i}`} sx={{ pl: 1.25 }}>
                            <Row
                              label={<CreditLabel text={shortenStripeDescription(l.description)} />}
                              value={formatCents(l.amount, preview?.currency || currency)}
                            />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {charges.length > 0 && (
                    <Box sx={tonedPanelSx('primary', 0.04, 0.14)}>
                      <Stack spacing={0.5}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Box
                            sx={(theme) => ({
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: theme.palette.primary.main
                            })}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              color: 'primary.dark',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5
                            }}
                          >
                            {CHECKOUT_COPY.summary.chargesSectionLabel}
                          </Typography>
                        </Stack>
                        {summarizeInvoiceLines(charges).map((l, i) => (
                          <Box key={`ch-${i}`} sx={{ pl: 1.25 }}>
                            <Row label={l.label} value={formatCents(l.amount, preview?.currency || currency)} />
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              );
            })()
          ) : dedicatedOnlyModify ? (
            <Row label={`${ADDONS.dedicated.label} (new)`} value={`${formatCents(newDedicatedCents, currency)}${suffix}`} />
          ) : state.isModifyingExisting && !state.signalManagerSelected ? (
            <Box
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 1.75,
                bgcolor: alpha(theme.palette.info.main, 0.05),
                border: `1px dashed ${alpha(theme.palette.info.main, 0.25)}`
              })}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12, lineHeight: 1.5 }}>
                No plan changes yet — adjust products above to preview what you'll be charged.
              </Typography>
            </Box>
          ) : (
            catalogLines.map((l) => <Row key={l.key} label={l.label} value={l.value} muted={l.muted} hint={l.hint} />)
          )}

          {/* Subtotal/Discount/Credit adjustments — grouped as a small ledger.
              Rendered for the live invoice AND for the discounted-preview
              state (showDiscountLedger) — first-time buyers applying a code
              get the backend-real Subtotal/Discount rows instead of only the
              optimistic note. */}
          {(showDiscountLedger && preview && ((preview.subtotal || 0) > 0 || (preview.discount || 0) !== 0)) ||
          availableCredit > 0 ||
          discountNotCarried ? (
            <Stack spacing={0.5} sx={{ pt: 0.25 }}>
              {showDiscountLedger && preview && (preview.subtotal || 0) > 0 && (
                <Row
                  label={CHECKOUT_COPY.summary.subtotalLabel}
                  value={formatCents(preview.subtotal, preview.currency || currency)}
                  muted
                />
              )}
              {showDiscountLedger && preview && (preview.discount || 0) !== 0 && (
                <>
                  <Row
                    label={
                      preview.cancelOfferApplied
                        ? CHECKOUT_COPY.summary.cancelOffer.label(offerPercent(preview.cancelOfferPercentBps || 0))
                        : CHECKOUT_COPY.summary.discountLabel
                    }
                    value={`−${formatCents(preview.discount, preview.currency || currency)}`}
                    tag={preview.cancelOfferApplied ? CHECKOUT_COPY.summary.cancelOffer.tag : undefined}
                    muted
                    teal
                  />
                  {/* Extension of the row above: which code, on what terms.
                      The amount alone left buyers asking what the code was
                      actually giving them. */}
                  {Boolean(quotedCoupon) && !preview.cancelOfferApplied && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', pl: 1.25, mt: -0.25, fontSize: 10.5, color: 'success.main', lineHeight: 1.4 }}
                    >
                      {CHECKOUT_COPY.summary.discountAppliedNote(quotedCoupon, discountTerms)}
                    </Typography>
                  )}
                </>
              )}
              {discountNotCarried && (
                <Stack direction="row" spacing={0.75} alignItems="flex-start" role="note" sx={tonedPanelSx('warning', 0.06, 0.25)}>
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      color: 'warning.main',
                      fontSize: 13,
                      lineHeight: 1,
                      mt: '1px',
                      animation: `${warningPulse} 1.2s ease-in-out 3`,
                      [REDUCED_MOTION]: { animation: 'none' }
                    }}
                  >
                    <WarningFilled />
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: 10.5, fontWeight: 600, color: 'warning.dark', lineHeight: 1.4 }}>
                    {CHECKOUT_COPY.summary.discountNotCarried}
                  </Typography>
                </Stack>
              )}
              {(() => {
                const effectiveCredit = resolveEffectiveCredit(preview, preview?.subtotal, availableCredit);
                const hasPlanChanges = showDiscountLedger && preview;
                const appliedRaw = preview ? Number(preview.appliedBalance || 0) : 0;
                const isDowngradeCredit = appliedRaw < 0;

                if (isDowngradeCredit) {
                  return (
                    <Row label="Credit returned" value={`+${formatCents(Math.abs(appliedRaw), preview?.currency || currency)}`} muted />
                  );
                }
                if (effectiveCredit > 0 && hasPlanChanges) {
                  return <Row label="Credit applied" value={`−${formatCents(effectiveCredit, preview?.currency || currency)}`} muted />;
                }
                // Catalog credit applies only to a NEW purchase (a modify owes nothing until a preview).
                if (!state.isModifyingExisting && availableCredit > 0) {
                  const appliedFromCatalog = Math.min(availableCredit, Number(state.grandTotalCycleCents) || 0);
                  return <Row label="Credit applied" value={`−${formatCents(appliedFromCatalog, preview?.currency || currency)}`} muted />;
                }
                return null;
              })()}
            </Stack>
          ) : null}

          {/* Discount code entry — the applied amount renders via the existing
              Discount row above (preview.discount); this only captures the code. */}
          {preview?.cancelOfferPercentBps ? (
            <CancelOfferNote percentBps={preview.cancelOfferPercentBps} applied={Boolean(preview.cancelOfferApplied)} />
          ) : (
            <DiscountCodeField showDiscountLedger={showDiscountLedger} />
          )}
        </Stack>
      </Box>

      {/* ── DUE TODAY (emphasized footer block) ───────────────────────── */}
      <Box
        sx={(theme) => ({
          mx: 2.25,
          mb: 2,
          mt: 0.5,
          p: 1.75,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(
            theme.palette.primary.main,
            0.02
          )} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`
        })}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
          <Stack spacing={0.25}>
            <Typography
              variant="caption"
              sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary' }}
            >
              {showLiveInvoice ? CHECKOUT_COPY.summary.dueTodayTitle : dueTodayLabel}
            </Typography>
            {showSavings && (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  alignSelf: 'flex-start',
                  px: 1,
                  py: '2px',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  color: '#fff',
                  borderRadius: 999,
                  background: 'linear-gradient(135deg, #00897b 0%, #00b58e 55%, #00897b 100%)',
                  boxShadow: '0 1px 0 rgba(255,255,255,0.22) inset, 0 4px 12px -4px rgba(0,137,123,0.45)',
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {CHECKOUT_COPY.summary.saveLabel(formatCents(savingsCents, currency))}
              </Box>
            )}
          </Stack>
          <Stack direction="row" alignItems="baseline" spacing={1}>
            {showSavings && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}
              >
                {formatCents(wouldBeCents, currency)}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 800,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1,
                color: 'text.primary',
                letterSpacing: -0.4
              }}
            >
              {formatCents(dueCents, preview?.currency || currency)}
            </Typography>
            {/* The /mo·/yr suffix describes the recurring catalog price — in
                the discounted-preview state the figure is the first invoice's
                real charge (a once-off coupon doesn't repeat), so no suffix,
                mirroring the live-invoice presentation. */}
            {!showLiveInvoice && !hasCredit && !trialApplies && !discountedPreviewApplies && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                {suffix}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* ── AFTER FREE TRIAL (new-user trial path) ───────────────────── */}
      {showAfterTrial && (
        <Box
          sx={(theme) => ({
            mx: 2.25,
            mb: 1.5,
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.text.primary, 0.025),
            border: `1px dashed ${alpha(theme.palette.text.primary, 0.15)}`
          })}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack spacing={0.25}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {trialDays > 0 ? `After your ${trialDays}-day free trial` : 'After your free trial'}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                Recurring per {cycleLabel || 'cycle'}
              </Typography>
            </Stack>
            <Typography sx={{ fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'text.primary' }}>
              {formatCents(afterTrialCents, currency)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.25, fontSize: 11 }}>
                {suffix}
              </Typography>
            </Typography>
          </Stack>
        </Box>
      )}

      {/* ── STARTING NEXT CYCLE ──────────────────────────────────────── */}
      {showLiveInvoice && preview && preview.nextInvoiceAmount > 0 && (
        <Box
          sx={(theme) => ({
            mx: 2.25,
            mb: 1.5,
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.text.primary, 0.025),
            border: `1px dashed ${alpha(theme.palette.text.primary, 0.15)}`
          })}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack spacing={0.25}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {(() => {
                  const d = formatPeriodDate(preview.nextInvoiceDate);
                  return d ? CHECKOUT_COPY.summary.startingTitle(d) : CHECKOUT_COPY.summary.recurringTitle;
                })()}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                Recurring per {cycleLabel || 'cycle'}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                color: 'text.primary'
              }}
            >
              {formatCents(preview.nextInvoiceAmount, preview.currency || currency)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.25, fontSize: 11 }}>
                {suffix}
              </Typography>
            </Typography>
          </Stack>
        </Box>
      )}

      {/* ── FOOTNOTES ────────────────────────────────────────────────── */}
      {(showLiveInvoice || (!showLiveInvoice && state.addItemProration)) && (
        <Box sx={{ px: 2.25, pb: 1.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, fontSize: 11 }}>
            {showLiveInvoice ? CHECKOUT_COPY.summary.finalNote : CHECKOUT_COPY.addItemProrationNote}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

function buildCatalogLines(state: any, suffix: string, currency: string | undefined) {
  type Line = { key: string; label: string; value: string; muted?: boolean; hint?: string };
  const lines: Line[] = [];
  // Products whose created subscription starts on a trial (empty on "Buy now").
  const trialKeys: Set<string> = state.trialProductKeys instanceof Set ? state.trialProductKeys : new Set();
  const trialDays = Number(state.trialDays) || 0;

  // A trialing line owes nothing today, so it must not be listed at its price:
  // on a cart mixing it with paid items the lines would sum past the total.
  // Show $0 today and carry the real price on a muted "after the trial" line.
  const pushPriced = (line: Line, cents: number) => {
    if (!trialKeys.has(line.key)) {
      lines.push(line);
      return;
    }
    lines.push({ ...line, value: CHECKOUT_COPY.freeTrialValue(formatCents(0, currency)) });
    lines.push({
      key: `${line.key}-after-trial`,
      label: CHECKOUT_COPY.afterTrialLineLabel(trialDays),
      value: `${formatCents(cents, currency)}${suffix}`,
      muted: true
    });
  };

  // Signal Manager is standalone — the breakdown is just its own line.
  if (state.signalManagerSelected) {
    lines.push({
      key: 'signalManager',
      label: `${CHECKOUT_COPY.signalManager.title} (${CHECKOUT_COPY.signalManager.includedNote(SIGNAL_MANAGER_BASE_SEATS)})`,
      value: `${formatCents(state.signalManagerCycleCents || 0, currency)}${CHECKOUT_COPY.addons.perMonthSuffix}`
    });
    return lines;
  }

  // So is EA Hosting: one line at the staged slot count, $0 today on a trial.
  if (state.eaHostingStandalone) {
    const cents = state.eaHostingCycleCents || 0;
    pushPriced(
      {
        key: EA_HOSTING_PRODUCT,
        label: `${EA_HOSTING_PLAN.label} × ${state.eaHostingSlots || 0}`,
        value: `${formatCents(cents, currency)}${suffix}`
      },
      cents
    );
    return lines;
  }

  const tierLines = [
    {
      key: 'standard',
      wants: !!state.wantsStandard,
      count: state.standardCount || 0,
      label: CHECKOUT_COPY.standardAccountsLabel,
      cents: state.standardCycleCents || 0,
      unitCents: state.stdUnitCycleCents || 0
    },
    {
      key: 'premium',
      wants: !!state.wantsPremium,
      count: state.premiumCount || 0,
      label: CHECKOUT_COPY.premiumAccountsLabel,
      cents: state.premiumCycleCents || 0,
      unitCents: state.premUnitCycleCents || 0
    }
  ];
  tierLines
    .filter((t) => t.wants && t.count > 0)
    .forEach((t) =>
      pushPriced(
        {
          key: t.key,
          label: `${t.label} × ${t.count}`,
          value: `${formatCents(t.cents, currency)}${suffix}`,
          // The value is the LINE TOTAL (tier-aware, computePricing). Without the
          // unit spelled out there is no way to check the arithmetic on the screen
          // immediately before paying. std/premUnitCycleCents are the per-account
          // rates AT THIS COUNT (a volume tier bills every account at one rate), so
          // this multiplies out to the value above.
          hint: t.unitCents ? CHECKOUT_COPY.perAccountHint(`${formatCents(t.unitCents, currency)}${suffix}`, t.count) : undefined
        },
        t.cents
      )
    );

  // Futures has two display modes:
  //   tiered      → "Futures Accounts × N  $X" (skipped when count=0)
  //   unlimited   → "Futures Accounts (Unlimited)  $X" (always shown when on)
  if (state.effectiveFuturesUnlimited) {
    pushPriced(
      {
        key: 'futures',
        label: `${CHECKOUT_COPY.futuresAccountsLabel} (${CHECKOUT_COPY.configure.futuresUnlimitedBadge})`,
        value: `${formatCents(state.futuresCycleCents || 0, currency)}${suffix}`
      },
      state.futuresCycleCents || 0
    );
  } else if (state.wantsFutures && (state.futuresCount || 0) > 0) {
    pushPriced(
      {
        key: 'futures',
        label: `${CHECKOUT_COPY.futuresAccountsLabel} × ${state.futuresCount}`,
        value: `${formatCents(state.futuresCycleCents || 0, currency)}${suffix}`
      },
      state.futuresCycleCents || 0
    );
  }

  if (state.analyzerActive) {
    pushPriced(
      { key: 'analyzer', label: ADDONS.analyzer.label, value: `${formatCents(state.analyzerCycleCents || 0, currency)}${suffix}` },
      state.analyzerCycleCents || 0
    );
  }
  if (state.dedicatedActive && (state.dedicatedEnvCount || 0) > 0) {
    const accountsPart = state.dedicatedAllFutures
      ? CHECKOUT_COPY.addons.envUnlimitedAccounts
      : CHECKOUT_COPY.summary.accountCount(state.dedicatedAccountsTotal);
    const envSummary = `${CHECKOUT_COPY.summary.envCount(state.dedicatedEnvCount)} · ${accountsPart}`;
    lines.push({
      key: 'dedicated',
      label: `${ADDONS.dedicated.label} (${envSummary})`,
      value: `${formatCents(state.dedicatedCycleCents || 0, currency)}${suffix}`
    });
  }
  lines.push({
    key: 'accounts',
    label: CHECKOUT_COPY.totalAccountsLabel,
    value: CHECKOUT_COPY.summary.accountCount(state.totalAccounts || 0),
    muted: true
  });

  return lines;
}
